import TooltipTag from "@/components/UI/TooltipTag";
import type { ReviewItemStatus } from "./medicalReview.types";

// Review state and data state are deliberately separate: a record whose data
// moved after a mark reads "Re-review" and keeps its earlier mark, rather than
// the mark being silently reset.
const STATUS_CONFIG: Record<
  ReviewItemStatus,
  { label: string; tagColor: string; textColor: string; tooltip: string }
> = {
  reviewed: {
    label: "Reviewed",
    tagColor: "#E4F1EA",
    textColor: "#2E7D5B",
    tooltip: "Reviewed, and nothing material has changed since.",
  },
  unreviewed: {
    label: "Awaiting review",
    tagColor: "#F1F3F5",
    textColor: "#556C75",
    tooltip: "New since the last review pass.",
  },
  re_review: {
    label: "Re-review",
    tagColor: "#F7E6EE",
    textColor: "#A31D5C",
    tooltip:
      "Material data changed after this was reviewed. The earlier mark is kept in the history.",
  },
};

export default function ReviewStatusTag({
  status,
  tooltip,
}: {
  status: ReviewItemStatus;
  tooltip?: string;
}) {
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.unreviewed;
  return (
    <TooltipTag
      tagColor={config.tagColor}
      textColor={config.textColor}
      tooltipTitle={tooltip ?? config.tooltip}
    >
      {config.label}
    </TooltipTag>
  );
}

export function SaeTag() {
  return (
    <TooltipTag
      tagColor="#F7E6EE"
      textColor="#A31D5C"
      tooltipTitle="Serious adverse event — reviewed against the expedited SLA in the Medical Monitoring Plan rather than the standard review window."
    >
      SAE
    </TooltipTag>
  );
}

export function LateArrivingTag() {
  return (
    <TooltipTag
      tagColor="#F6EDD8"
      textColor="#A4720F"
      tooltipTitle="Backdated arrival — the clinical event happened well before this record reached Vivo, so it is reaching review late."
    >
      Late
    </TooltipTag>
  );
}

// Display labels for the mark vocabulary. The API grows new actions ahead of
// the UI, so an unmapped action falls back to the label the API supplies and
// then to the raw action rather than rendering blank.
const MARK_ACTION_LABELS: Record<string, string> = {
  reviewed: "Reviewed",
  bulk_reviewed: "Reviewed (bulk)",
  comment: "Comment",
  action_raised: "Action raised",
  review_reopened: "Review reopened",
  assigned: "Assigned",
  claimed: "Claimed",
  review_escalated: "Escalated",
  review_unassigned: "Unassigned",
};

// Actions that take a review back or hand it on, rather than adding one.
const RETRACTING_ACTIONS = new Set([
  "review_reopened",
  "review_escalated",
  "review_unassigned",
]);

export function MarkActionLabel({
  action,
  /** The signature meaning the API recorded for this mark. */
  meaning,
  /** The API's own display label, when the response carries one. */
  actionLabel,
}: {
  action: string;
  meaning?: string;
  actionLabel?: string;
}) {
  const label = MARK_ACTION_LABELS[action] ?? actionLabel ?? action;
  return (
    <>
      <span
        className={`font-semibold ${
          RETRACTING_ACTIONS.has(action) ? "text-[#A4720F]" : "text-[#2E7D5B]"
        }`}
      >
        {label}
      </span>
      {meaning && meaning !== label && (
        <span className="text-[#8FA5A0]">({meaning})</span>
      )}
    </>
  );
}

export function EscalatedTag() {
  return (
    <TooltipTag
      tagColor="#F6EDD8"
      textColor="#A4720F"
      tooltipTitle="Escalated for a more senior opinion. The note asking for it is in this record's review history."
    >
      Escalated
    </TooltipTag>
  );
}
